import { format } from 'date-fns'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { DateInput } from '@/components/ui/DateInput'
import { blurIfDateFieldStale } from '../../lib/dateField'
import { ChatActionWithValue } from '../../models/chat'
import { COVERAGE_START_HINT, MAX_COVERAGE_START_DAYS } from '../../models/insuranceCoverage'

interface ChatCoverageStartDateProps {
  defaultDate?: string
  // Whether defaultDate is a computed guess (e.g. the carrier's current
  // insurance expiration date) rather than a value the user actually chose.
  // Guesses get clamped into the valid [today, maxDate] range; a real prior
  // answer (editing an already-submitted date) is preserved as-is so
  // re-opening the edit dialog doesn't silently overwrite it.
  isFallbackDefault?: boolean
  // The server's allowed range (yyyy-MM-dd). The browser's clock can be a day off the
  // server's, so a range computed here could offer dates the server rejects.
  minDate?: string
  maxDate?: string
  handleSubmit: (data: ChatActionWithValue) => void
  readonly?: boolean
}

// Module scope: DateInput is memoised, and a fresh object each render would defeat it.
const COVERAGE_START_INPUT_PROPS = { 'aria-label': 'Coverage start date', name: 'coverageStartDate' }

function parseLocalDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function ChatCoverageStartDate({
  defaultDate,
  isFallbackDefault = true,
  minDate: serverMinDate,
  maxDate: serverMaxDate,
  handleSubmit,
  readonly,
}: ChatCoverageStartDateProps) {
  const today = useMemo(() => {
    if (serverMinDate) return parseLocalDate(serverMinDate)
    const t = new Date()
    t.setHours(0, 0, 0, 0)
    return t
  }, [serverMinDate])
  const maxDate = useMemo(() => {
    if (serverMaxDate) return parseLocalDate(serverMaxDate)
    const d = new Date(today)
    d.setDate(d.getDate() + MAX_COVERAGE_START_DAYS)
    return d
  }, [today, serverMaxDate])
  const initialDate = useMemo(() => {
    const parsed = defaultDate ? parseLocalDate(defaultDate) : undefined
    if (!parsed || !isFallbackDefault) return parsed
    if (parsed < today) return today
    if (parsed > maxDate) return maxDate
    return parsed
  }, [defaultDate, isFallbackDefault, today, maxDate])
  const hidden = useMemo(() => [{ before: today }, { after: maxDate }], [today, maxDate])
  const [date, setDate] = useState<Date | undefined>(initialDate)

  function onSubmit() {
    if (!date) return
    handleSubmit({
      content: {
        component: 'P',
        content: format(date, 'MMMM do, yyyy'),
      },
      value: format(date, 'yyyy-MM-dd'),
    })
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault()
        if (blurIfDateFieldStale(event.currentTarget, 'coverageStartDate', date)) return
        if (!readonly) onSubmit()
      }}
    >
      <DateInput
        value={date}
        onChange={setDate}
        hidden={hidden}
        defaultMonth={initialDate ?? today}
        errorMessage={`Start date must be within the next ${MAX_COVERAGE_START_DAYS} days`}
        description={COVERAGE_START_HINT}
        inputProps={COVERAGE_START_INPUT_PROPS}
      />
      {!readonly && (
        <div className="flex justify-end">
          <Button type="submit" disabled={!date}>
            Confirm date
          </Button>
        </div>
      )}
    </form>
  )
}
